import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Checkbox, Dialog, Input } from '@artosera/ui';
import {
  lireManifeste,
  lirePageManifeste,
  listerDepots,
  type EntreeManifeste,
} from '../../../../lib/depots.js';
import { formaterOctets } from '../../../../lib/format.js';
import {
  creerLotFichiers,
  creerLotImages,
  rapprocherLot,
  TYPES_MANIFESTE,
} from '../../../../lib/images.js';
import type { LotReprise } from '../../../../lib/reprise.js';
import formStyles from '../../../../styles/form.module.css';
import correspondance from '../correspondance/Correspondance.module.css';
import { cleDepots } from '../DepotsBloc.js';
import { messageReprise } from '../messages.js';
import reprise from '../Reprise.module.css';
import styles from './Images.module.css';

export const NATURES_DEPOT_FICHIERS = ['images', 'documents', 'factures-historiques'] as const;
export type NatureDepotFichiers = (typeof NATURES_DEPOT_FICHIERS)[number];

const estTableur = (fichier: EntreeManifeste): boolean =>
  fichier.etat === 'accepte' &&
  fichier.type !== null &&
  TYPES_MANIFESTE.includes(fichier.type.mime);

/**
 * « Images ou documents d'un dépôt » : une nature, un dépôt utilisable, puis
 * ce que la nature demande — le sous-dossier des images, ou le manifeste
 * tabulaire des documents. Rien n'est écrit chez la galerie : le lot se
 * dépose, puis s'essaie.
 */
export function NouveauLotFichiers({
  idGalerie,
  ouvert,
  onFermer,
  onCree,
}: {
  idGalerie: string;
  ouvert: boolean;
  onFermer: () => void;
  onCree: (lot: LotReprise) => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const [nature, setNature] = useState<NatureDepotFichiers>('images');
  const [depotId, setDepotId] = useState('');
  const [dossier, setDossier] = useState('');
  const [remplacer, setRemplacer] = useState(false);
  const [manifesteId, setManifesteId] = useState('');
  const [confidentiels, setConfidentiels] = useState(false);
  const [page, setPage] = useState(1);
  const [erreur, setErreur] = useState<string | null>(null);
  const [cree, setCree] = useState<LotReprise | null>(null);

  const images = nature === 'images';

  const depots = useQuery({
    queryKey: [...cleDepots(idGalerie), 'pour-un-lot'],
    queryFn: () => listerDepots(idGalerie),
    enabled: ouvert,
  });
  const utilisables = (depots.data ?? []).filter((depot) => depot.utilisable);

  const cleManifeste = ['console', 'galerie', idGalerie, 'reprise', 'depots', depotId, 'manifeste'];
  const index = useQuery({
    queryKey: cleManifeste,
    queryFn: () => lireManifeste(idGalerie, depotId),
    enabled: ouvert && !images && depotId !== '',
  });
  const fichiers = useQuery({
    queryKey: [...cleManifeste, page],
    queryFn: () => lirePageManifeste(idGalerie, depotId, page),
    enabled: ouvert && !images && depotId !== '' && index.isSuccess,
  });
  const pages = index.data?.pages.length ?? 1;
  const tableurs = (fichiers.data ?? []).filter(estTableur);

  const reinitialiser = (): void => {
    setNature('images');
    setDepotId('');
    setDossier('');
    setRemplacer(false);
    setManifesteId('');
    setConfidentiels(false);
    setPage(1);
    setErreur(null);
    setCree(null);
  };

  const creer = useMutation({
    mutationFn: async () => {
      if (!images) {
        const lot = await creerLotFichiers(idGalerie, {
          depotId,
          nature: nature as 'documents' | 'factures-historiques',
          manifesteId,
          confidentiels: nature === 'documents' && confidentiels,
        });
        return { lot, refus: null };
      }
      const lot = await creerLotImages(idGalerie, {
        depotId,
        dossier,
        remplacerPrincipale: remplacer,
      });
      // Le lot existe dès ici : un rapprochement refusé (mandat absent…) se relance depuis sa fiche.
      try {
        await rapprocherLot(idGalerie, lot.id);
        return { lot, refus: null };
      } catch (cause) {
        return { lot, refus: messageReprise(t, cause, 'images') };
      }
    },
    onSuccess: ({ lot, refus }) => {
      if (refus === null) {
        reinitialiser();
        onCree(lot);
        return;
      }
      setCree(lot);
      setErreur(refus);
    },
    onError: (cause) => setErreur(messageReprise(t, cause, images ? 'images' : undefined)),
  });

  const fermer = (): void => {
    reinitialiser();
    onFermer();
  };

  const pret = depotId !== '' && (images || manifesteId !== '');

  return (
    <Dialog
      open={ouvert}
      onOpenChange={(estOuvert) => {
        if (!estOuvert) fermer();
      }}
      title={t('images.nouveau.titre')}
      description={t('images.nouveau.texte')}
      closeLabel={t('images.nouveau.annuler')}
      footer={
        cree ? (
          <Button
            onClick={() => {
              const lot = cree;
              reinitialiser();
              onCree(lot);
            }}
          >
            {t('images.nouveau.ouvrir')}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={fermer}>
              {t('images.nouveau.annuler')}
            </Button>
            <Button loading={creer.isPending} disabled={!pret} onClick={() => creer.mutate()}>
              {images ? t('images.nouveau.rapprocher') : t('images.nouveau.creer')}
            </Button>
          </>
        )
      }
    >
      <div className={reprise.pile}>
        <section className={correspondance.temps} aria-labelledby="lot-fichiers-1">
          <h3 id="lot-fichiers-1" className={correspondance.tempsTitre}>
            <span className={correspondance.tempsNumero} aria-hidden="true">
              1
            </span>
            {t('images.nouveau.natureTitre')}
          </h3>
          <div className={formStyles.champ}>
            <label htmlFor="lot-fichiers-nature">{t('images.nouveau.nature')}</label>
            <select
              id="lot-fichiers-nature"
              className={correspondance.selection}
              value={nature}
              disabled={creer.isPending || cree !== null}
              onChange={(evenement) => {
                setNature(evenement.target.value as NatureDepotFichiers);
                setManifesteId('');
                setPage(1);
                setErreur(null);
              }}
            >
              {NATURES_DEPOT_FICHIERS.map((valeur) => (
                <option key={valeur} value={valeur}>
                  {t(`images.nouveau.natures.${valeur}`)}
                </option>
              ))}
            </select>
            <span className={formStyles.aide}>{t(`images.nouveau.natureAide.${nature}`)}</span>
          </div>
        </section>

        <section className={correspondance.temps} aria-labelledby="lot-fichiers-2">
          <h3 id="lot-fichiers-2" className={correspondance.tempsTitre}>
            <span className={correspondance.tempsNumero} aria-hidden="true">
              2
            </span>
            {t('correspondance.nouveau.depot')}
          </h3>
          <div className={formStyles.champ}>
            <label htmlFor="lot-fichiers-depot">{t('correspondance.nouveau.depotChoix')}</label>
            <select
              id="lot-fichiers-depot"
              className={correspondance.selection}
              value={depotId}
              disabled={creer.isPending || cree !== null}
              onChange={(evenement) => {
                setDepotId(evenement.target.value);
                setManifesteId('');
                setPage(1);
                setErreur(null);
              }}
            >
              <option value="">{t('correspondance.nouveau.depotAucunChoix')}</option>
              {utilisables.map((depot) => (
                <option key={depot.id} value={depot.id}>
                  {`${depot.libelle || t('reprise.depots.sansNom')} · ${t('reprise.depots.fichiers', { count: depot.fichiers })}`}
                </option>
              ))}
            </select>
            {depots.isSuccess && utilisables.length === 0 ? (
              <span className={formStyles.aide}>{t('correspondance.nouveau.depotAucun')}</span>
            ) : null}
          </div>
        </section>

        {depotId !== '' && images ? (
          <section className={correspondance.temps} aria-labelledby="lot-fichiers-3">
            <h3 id="lot-fichiers-3" className={correspondance.tempsTitre}>
              <span className={correspondance.tempsNumero} aria-hidden="true">
                3
              </span>
              {t('images.nouveau.lecture')}
            </h3>
            <div className={formStyles.champ}>
              <label htmlFor="lot-fichiers-dossier">{t('images.nouveau.dossier')}</label>
              <Input
                id="lot-fichiers-dossier"
                value={dossier}
                maxLength={1024}
                placeholder={t('images.nouveau.dossierExemple')}
                disabled={creer.isPending || cree !== null}
                onChange={(evenement) => setDossier(evenement.target.value)}
              />
              <span className={formStyles.aide}>{t('images.nouveau.dossierAide')}</span>
            </div>
            <div className={formStyles.champ}>
              <Checkbox
                id="lot-fichiers-remplacer"
                label={t('images.nouveau.remplacer')}
                checked={remplacer}
                disabled={creer.isPending || cree !== null}
                onCheckedChange={(coche) => setRemplacer(coche === true)}
              />
              <span className={formStyles.aide}>{t('images.nouveau.remplacerAide')}</span>
            </div>
            {remplacer ? (
              <p className={reprise.avertissement} role="note">
                {t('images.nouveau.remplacerAvertissement')}
              </p>
            ) : null}
          </section>
        ) : null}

        {depotId !== '' && !images ? (
          <section className={correspondance.temps} aria-labelledby="lot-fichiers-3">
            <h3 id="lot-fichiers-3" className={correspondance.tempsTitre}>
              <span className={correspondance.tempsNumero} aria-hidden="true">
                3
              </span>
              {t('images.nouveau.manifeste')}
            </h3>
            <p className={reprise.discret}>{t('images.nouveau.manifesteAide')}</p>
            {index.isLoading || fichiers.isLoading ? (
              <p className={reprise.discret}>{t('reprise.chargement')}</p>
            ) : null}
            {index.isError || fichiers.isError ? (
              <p className={reprise.erreur} role="alert">
                {t('reprise.depots.manifeste.erreur')}
              </p>
            ) : null}
            {fichiers.isSuccess ? (
              tableurs.length === 0 ? (
                <p className={reprise.discret}>{t('images.nouveau.aucunTableur')}</p>
              ) : (
                <fieldset className={styles.candidats}>
                  <legend className={correspondance.sansTexte}>
                    {t('images.nouveau.manifeste')}
                  </legend>
                  <ul className={styles.choixManifeste} tabIndex={0}>
                    {tableurs.map((fichier) => (
                      <li key={fichier.id}>
                        <label className={styles.candidat}>
                          <input
                            type="radio"
                            name="lot-fichiers-manifeste"
                            value={fichier.id}
                            checked={manifesteId === fichier.id}
                            disabled={creer.isPending}
                            onChange={() => setManifesteId(fichier.id)}
                          />
                          <span className={styles.candidatTexte}>
                            <span className={reprise.chemin}>{fichier.chemin}</span>
                            <span className={styles.candidatTitre}>
                              {formaterOctets(fichier.octets)}
                            </span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </fieldset>
              )
            ) : null}
            {pages > 1 ? (
              <div className={styles.pagination}>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((courante) => courante - 1)}
                >
                  {t('reprise.depots.manifeste.precedente')}
                </Button>
                <span className={reprise.discret}>
                  {t('reprise.depots.manifeste.page', { page, pages })}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page >= pages}
                  onClick={() => setPage((courante) => courante + 1)}
                >
                  {t('reprise.depots.manifeste.suivante')}
                </Button>
              </div>
            ) : null}
            {nature === 'documents' ? (
              <div className={formStyles.champ}>
                <Checkbox
                  id="lot-fichiers-confidentiels"
                  label={t('reprise.depot.confidentiels')}
                  checked={confidentiels}
                  disabled={creer.isPending}
                  onCheckedChange={(coche) => setConfidentiels(coche === true)}
                />
                <span className={formStyles.aide}>{t('reprise.depot.confidentielsAide')}</span>
              </div>
            ) : null}
          </section>
        ) : null}

        {erreur ? (
          <p className={reprise.erreur} role="alert">
            {cree ? t('images.nouveau.rapprochementRefuse', { message: erreur }) : erreur}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
